import {
  CHARLES_PRESSURE_KPA,
  EXPLORER_DEFAULTS,
  GAS_CONSTANT,
  KELVIN_OFFSET,
  KPA_PER_ATM,
  ML_PER_L,
  formatSig,
  type ExplorerState,
  type GasMode,
} from './gasLaws';
import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError } from './modelInputContract';

/**
 * Learner-driven Explore for the Gas Laws Lab (chemistry/CLAUDE.md "Interactive calculation
 * standard"). The learner types the real quantities with units; one pure function converts them,
 * solves the chosen law with the numbers substituted and returns the gas state that drives the
 * particle box, the graph, the readouts and the 3D gas syringe.
 *
 * Teaching limits (pending teaching review): 10–1000 kPa, 0.1–25 L, 0.001–10 mol and 150–1000 K.
 * Above about 1000 kPa real gases stop behaving ideally; below about 150 K many gases are close to
 * condensing. The model stops at these limits instead of extrapolating.
 */
export type GasExploreSlot = 'p1' | 'V1' | 'V2' | 'T1' | 'T2' | 'p' | 'V' | 'n' | 'T';
export type GasExploreVariable = 'p' | 'V' | 'n' | 'T';
export type PressureChoice = 'kPa' | 'atm';
export type VolumeChoice = 'L' | 'mL';
export type TemperatureChoice = 'K' | 'C';

export interface GasExploreUnits {
  p: PressureChoice;
  V: VolumeChoice;
  T: TemperatureChoice;
}

export interface GasExploreSetup {
  mode: GasMode;
  /** The quantity the model solves for. Boyle solves p₂ and Charles solves V₂. */
  unknown: GasExploreSlot | 'p2';
  units: GasExploreUnits;
  /** Typed values, in the learner's chosen units. */
  values: Partial<Record<GasExploreSlot, number>>;
}

export const GAS_EXPLORE_LIMITS: Record<GasExploreVariable, { min: number; max: number; unit: string }> = {
  p: { min: 10, max: 1000, unit: 'kPa' },
  V: { min: 0.1, max: 25, unit: 'L' },
  n: { min: 0.001, max: 10, unit: 'mol' },
  T: { min: 150, max: 1000, unit: 'K' },
};

/** Temperature held constant in Boyle mode, the same as the Practice explorer. */
export const BOYLE_TEMPERATURE_K = EXPLORER_DEFAULTS.boyle.temperatureK;

const UNIT_RANGES = {
  kPa: { min: 10, max: 1000, step: 0.1, unit: 'kPa' },
  atm: { min: 0.1, max: 9.8, step: 0.001, unit: 'atm' },
  L: { min: 0.1, max: 25, step: 0.01, unit: 'L' },
  mL: { min: 100, max: 25000, step: 1, unit: 'mL' },
  mol: { min: 0.001, max: 10, step: 0.001, unit: 'mol' },
  K: { min: 150, max: 1000, step: 0.01, unit: 'K' },
  C: { min: -123, max: 726, step: 0.01, unit: '°C' },
} as const;

const SLOT_COPY: Record<GasExploreSlot | 'p2', { label: string; symbol: string }> = {
  p1: { label: 'Starting pressure', symbol: 'p₁' },
  p2: { label: 'New pressure', symbol: 'p₂' },
  V1: { label: 'Starting volume', symbol: 'V₁' },
  V2: { label: 'New volume', symbol: 'V₂' },
  T1: { label: 'Starting temperature', symbol: 'T₁' },
  T2: { label: 'New temperature', symbol: 'T₂' },
  p: { label: 'Pressure', symbol: 'p' },
  V: { label: 'Volume', symbol: 'V' },
  n: { label: 'Amount of gas', symbol: 'n' },
  T: { label: 'Temperature', symbol: 'T' },
};

export const IDEAL_UNKNOWNS: readonly GasExploreVariable[] = ['p', 'V', 'n', 'T'];

export function slotSymbol(slot: GasExploreSlot | 'p2'): string {
  return SLOT_COPY[slot].symbol;
}

export function exploreVariable(slot: GasExploreSlot | 'p2'): GasExploreVariable {
  return slot[0] as GasExploreVariable;
}

function unitKey(variable: GasExploreVariable, units: GasExploreUnits): keyof typeof UNIT_RANGES {
  if (variable === 'n') return 'mol';
  return units[variable];
}

export function unitLabel(variable: GasExploreVariable, units: GasExploreUnits): string {
  return UNIT_RANGES[unitKey(variable, units)].unit;
}

/** The typed inputs for a mode, in order. The unknown never appears as an input. */
export function gasExploreInputs(setup: Pick<GasExploreSetup, 'mode' | 'unknown'>): GasExploreSlot[] {
  if (setup.mode === 'boyle') return ['p1', 'V1', 'V2'];
  if (setup.mode === 'charles') return ['V1', 'T1', 'T2'];
  return IDEAL_UNKNOWNS.filter((slot) => slot !== setup.unknown);
}

/** Field list for the shared input panel, with ranges in the learner's chosen units. */
export function gasExploreFields(setup: Pick<GasExploreSetup, 'mode' | 'unknown' | 'units'>): ModelField<GasExploreSlot>[] {
  return gasExploreInputs(setup).map((slot) => ({ key: slot, label: SLOT_COPY[slot].label, ...UNIT_RANGES[unitKey(exploreVariable(slot), setup.units)] }));
}

/** The input that gets a slider: the quantity the learner changes in the experiment. */
export function gasExploreSliderKey(setup: Pick<GasExploreSetup, 'mode' | 'unknown'>): GasExploreSlot {
  if (setup.mode === 'boyle') return 'V2';
  if (setup.mode === 'charles') return 'T2';
  return setup.unknown === 'T' ? 'V' : 'T';
}

function toBase(variable: GasExploreVariable, value: number, units: GasExploreUnits): number {
  if (variable === 'p') return units.p === 'atm' ? value * KPA_PER_ATM : value;
  if (variable === 'V') return units.V === 'mL' ? value / ML_PER_L : value;
  if (variable === 'T') return units.T === 'C' ? value + KELVIN_OFFSET : value;
  return value;
}

function fromBase(variable: GasExploreVariable, value: number, units: GasExploreUnits): number {
  if (variable === 'p') return units.p === 'atm' ? value / KPA_PER_ATM : value;
  if (variable === 'V') return units.V === 'mL' ? value * ML_PER_L : value;
  if (variable === 'T') return units.T === 'C' ? value - KELVIN_OFFSET : value;
  return value;
}

const tidy = (value: number) => Number(value.toFixed(6));

/** Switches one unit and converts that variable's typed values, so the gas itself does not change. */
export function changeGasUnit(setup: GasExploreSetup, variable: 'p' | 'V' | 'T', unit: string): GasExploreSetup {
  const units = { ...setup.units, [variable]: unit } as GasExploreUnits;
  const values = { ...setup.values };
  (Object.keys(values) as GasExploreSlot[]).forEach((slot) => {
    const value = values[slot];
    if (exploreVariable(slot) !== variable || value === undefined) return;
    values[slot] = tidy(fromBase(variable, toBase(variable, value, setup.units), units));
  });
  return { ...setup, units, values };
}

/** Chooses a new unknown for the ideal gas equation; the old unknown becomes an input. */
export function changeGasUnknown(setup: GasExploreSetup, unknown: GasExploreVariable, previous: GasExploreResult | null): GasExploreSetup {
  const values = { ...setup.values };
  if (setup.unknown !== unknown) {
    // The old unknown becomes an input: its solved value, or a neutral value if it was outside the model.
    const old = setup.unknown as GasExploreVariable;
    const solved = previous && previous.withinModel ? previous.baseValues[old] : IDEAL_FALLBACK[old];
    values[old] = tidy(fromBase(old, solved, setup.units));
  }
  delete values[unknown];
  return { ...setup, unknown, values };
}

/** Neutral inputs (kPa, L, mol, K) used when a previous answer was outside the model. */
const IDEAL_FALLBACK: Record<GasExploreVariable, number> = { p: 100, V: 10, n: 0.5, T: 298.15 };

export const DEFAULT_GAS_EXPLORE: Record<GasMode, GasExploreSetup> = {
  boyle: { mode: 'boyle', unknown: 'p2', units: { p: 'kPa', V: 'L', T: 'K' }, values: { p1: 100, V1: 20, V2: 10 } },
  charles: { mode: 'charles', unknown: 'V2', units: { p: 'kPa', V: 'L', T: 'C' }, values: { V1: 10, T1: 20, T2: 100 } },
  ideal: { mode: 'ideal', unknown: 'V', units: { p: 'kPa', V: 'L', T: 'C' }, values: { p: 100, n: 0.5, T: 25 } },
};

export interface GasExplorePreset {
  id: string;
  label: string;
  setup: GasExploreSetup;
}

/** Shortcuts that only set the inputs; the learner can change every value afterwards. */
export const GAS_EXPLORE_PRESETS: Record<GasMode, GasExplorePreset[]> = {
  boyle: [
    { id: 'halve', label: 'Halve the volume', setup: DEFAULT_GAS_EXPLORE.boyle },
    { id: 'double', label: 'Double the volume', setup: { ...DEFAULT_GAS_EXPLORE.boyle, values: { p1: 100, V1: 10, V2: 20 } } },
    { id: 'tyre', label: 'Squeeze to 1 L', setup: { ...DEFAULT_GAS_EXPLORE.boyle, values: { p1: 100, V1: 5, V2: 1 } } },
  ],
  charles: [
    { id: 'boil', label: '20 °C → 100 °C', setup: DEFAULT_GAS_EXPLORE.charles },
    { id: 'double-kelvin', label: 'Double the kelvin temperature', setup: { ...DEFAULT_GAS_EXPLORE.charles, units: { p: 'kPa', V: 'L', T: 'K' }, values: { V1: 5, T1: 300, T2: 600 } } },
    { id: 'freezer', label: 'Into a freezer (−18 °C)', setup: { ...DEFAULT_GAS_EXPLORE.charles, values: { V1: 10, T1: 25, T2: -18 } } },
  ],
  ideal: [
    { id: 'slc', label: '1 mol at SLC (25 °C, 100 kPa)', setup: { ...DEFAULT_GAS_EXPLORE.ideal, values: { p: 100, n: 1, T: 25 } } },
    { id: 'stp', label: '1 mol at STP (0 °C, 100 kPa)', setup: { ...DEFAULT_GAS_EXPLORE.ideal, values: { p: 100, n: 1, T: 0 } } },
    { id: 'can', label: 'Sealed 0.5 L can at 600 K', setup: { ...DEFAULT_GAS_EXPLORE.ideal, unknown: 'p', units: { p: 'kPa', V: 'L', T: 'K' }, values: { V: 0.5, n: 0.02, T: 600 } } },
  ],
};

export interface GasExploreResult {
  unknown: GasExploreSlot | 'p2';
  /** Answer in the learner's unit for that quantity. */
  value: number;
  unit: string;
  steps: WorkedStep[];
  result: string;
  /** Final gas state in kPa, L, mol and K. */
  baseValues: Record<GasExploreVariable, number>;
  /** Gas before the change in kPa, L, mol and K (Boyle p₁ V₁, Charles V₁ T₁); the same as the final gas in ideal mode. */
  start: Record<GasExploreVariable, number>;
  /** State for the particle box, graph and 3D syringe (the gas after the change). */
  state: ExplorerState;
  withinModel: boolean;
  limitNote: string | null;
}

const fmt = (value: number) => formatSig(value, 4);

function limitNote(values: Record<GasExploreVariable, number>, symbols: Record<GasExploreVariable, string>): string | null {
  const { p, T } = GAS_EXPLORE_LIMITS;
  if (values.p > p.max) {
    return `${symbols.p} = ${fmt(values.p)} kPa is above ${p.max} kPa. Real gases stop behaving ideally at high pressure (particle volume and attractions matter), so the model stops here.`;
  }
  if (values.T < T.min) {
    return `${symbols.T} = ${fmt(values.T)} K is below ${T.min} K. Many gases are close to condensing there, so the ideal gas model stops here.`;
  }
  for (const variable of IDEAL_UNKNOWNS) {
    const limit = GAS_EXPLORE_LIMITS[variable];
    if (values[variable] < limit.min || values[variable] > limit.max) {
      return `${symbols[variable]} = ${fmt(values[variable])} ${limit.unit} is outside this model (${limit.min}–${limit.max} ${limit.unit}). The model stops here instead of guessing.`;
    }
  }
  return null;
}

function conversionStep(setup: GasExploreSetup, inputs: GasExploreSlot[], base: Partial<Record<GasExploreSlot, number>>): WorkedStep {
  const lines: string[] = [];
  const formulas = new Set<string>();
  inputs.forEach((slot) => {
    const variable = exploreVariable(slot);
    const typed = setup.values[slot] as number;
    const symbol = SLOT_COPY[slot].symbol;
    if (variable === 'T' && setup.units.T === 'C') {
      formulas.add('T(K) = T(°C) + 273.15');
      lines.push(`${symbol} = ${fmt(typed)} °C + 273.15 = ${formatSig(base[slot] as number, 5)} K`);
    } else if (variable === 'p' && setup.units.p === 'atm') {
      formulas.add('1 atm = 101.325 kPa');
      lines.push(`${symbol} = ${fmt(typed)} atm × 101.325 = ${fmt(base[slot] as number)} kPa`);
    } else if (variable === 'V' && setup.units.V === 'mL') {
      formulas.add('1 L = 1000 mL');
      lines.push(`${symbol} = ${fmt(typed)} mL ÷ 1000 = ${fmt(base[slot] as number)} L`);
    }
  });
  return {
    id: 'convert',
    title: 'Convert to kPa, L, mol and K',
    formula: formulas.size ? [...formulas].join(' · ') : 'no conversion needed',
    working: lines.length ? lines.join('; ') : 'All values are already in kPa, L, mol and K.',
    status: 'done',
  };
}

function answerStep(variable: GasExploreVariable, baseValue: number, setup: GasExploreSetup, symbol: string): WorkedStep | null {
  if (variable === 'T' && setup.units.T === 'C') {
    return { id: 'answer-unit', title: 'Give the answer in °C', formula: 'T(°C) = T(K) − 273.15', working: `${symbol} = ${formatSig(baseValue, 5)} K − 273.15 = ${fmt(baseValue - KELVIN_OFFSET)} °C`, status: 'done' };
  }
  if (variable === 'p' && setup.units.p === 'atm') {
    return { id: 'answer-unit', title: 'Give the answer in atm', formula: 'p(atm) = p(kPa) ÷ 101.325', working: `${symbol} = ${fmt(baseValue)} kPa ÷ 101.325 = ${fmt(baseValue / KPA_PER_ATM)} atm`, status: 'done' };
  }
  if (variable === 'V' && setup.units.V === 'mL') {
    return { id: 'answer-unit', title: 'Give the answer in mL', formula: 'V(mL) = V(L) × 1000', working: `${symbol} = ${fmt(baseValue)} L × 1000 = ${fmt(baseValue * ML_PER_L)} mL`, status: 'done' };
  }
  return null;
}

const IDEAL_REARRANGED: Record<GasExploreVariable, string> = { p: 'p = nRT ÷ V', V: 'V = nRT ÷ p', n: 'n = pV ÷ RT', T: 'T = pV ÷ nR' };

function idealSolve(unknown: GasExploreVariable, b: Partial<Record<GasExploreVariable, number>>): { value: number; working: string } {
  const R = GAS_CONSTANT;
  const p = b.p as number;
  const V = b.V as number;
  const n = b.n as number;
  const T = b.T as number;
  if (unknown === 'p') {
    const value = (n * R * T) / V;
    return { value, working: `p = ${fmt(n)} mol × ${R} × ${formatSig(T, 5)} K ÷ ${fmt(V)} L = ${fmt(value)} kPa` };
  }
  if (unknown === 'V') {
    const value = (n * R * T) / p;
    return { value, working: `V = ${fmt(n)} mol × ${R} × ${formatSig(T, 5)} K ÷ ${fmt(p)} kPa = ${fmt(value)} L` };
  }
  if (unknown === 'n') {
    const value = (p * V) / (R * T);
    return { value, working: `n = ${fmt(p)} kPa × ${fmt(V)} L ÷ (${R} × ${formatSig(T, 5)} K) = ${fmt(value)} mol` };
  }
  const value = (p * V) / (n * R);
  return { value, working: `T = ${fmt(p)} kPa × ${fmt(V)} L ÷ (${fmt(n)} mol × ${R}) = ${formatSig(value, 5)} K` };
}

/**
 * Solves one Explore setup with the learner's numbers substituted. Throws the first input error
 * (fail closed). A result outside the teaching limits is reported with `withinModel: false` and a
 * limit note; the page then keeps the last state inside the model.
 */
export function calculateGasExplore(setup: GasExploreSetup): GasExploreResult {
  if (setup.mode === 'ideal' && !IDEAL_UNKNOWNS.includes(setup.unknown as GasExploreVariable)) {
    throw new Error('Choose p, V, n or T as the unknown.');
  }
  const fields = gasExploreFields(setup);
  const typed = Object.fromEntries(fields.map((field) => [field.key, setup.values[field.key] ?? Number.NaN])) as Record<GasExploreSlot, number>;
  const error = firstModelError(fields, typed);
  if (error) throw new Error(error);

  const inputs = gasExploreInputs(setup);
  const base: Partial<Record<GasExploreSlot, number>> = {};
  inputs.forEach((slot) => {
    base[slot] = toBase(exploreVariable(slot), typed[slot], setup.units);
  });
  const steps: WorkedStep[] = [conversionStep(setup, inputs, base)];
  const R = GAS_CONSTANT;
  let final: Record<GasExploreVariable, number>;
  let start: Record<GasExploreVariable, number>;
  let unknownVariable: GasExploreVariable;
  let symbols: Record<GasExploreVariable, string>;

  if (setup.mode === 'boyle') {
    const p1 = base.p1 as number;
    const V1 = base.V1 as number;
    const V2 = base.V2 as number;
    const T = BOYLE_TEMPERATURE_K;
    const p2 = (p1 * V1) / V2;
    const n = (p1 * V1) / (R * T);
    steps.push(
      { id: 'law', title: 'Boyle’s law (n and T fixed)', formula: 'p₂ = p₁V₁ ÷ V₂', working: `p₂ = ${fmt(p1)} kPa × ${fmt(V1)} L ÷ ${fmt(V2)} L = ${fmt(p2)} kPa`, status: 'done' },
      { id: 'check', title: 'Check: p × V stays the same', formula: 'p₁V₁ = p₂V₂', working: `${fmt(p1)} × ${fmt(V1)} = ${fmt(p1 * V1)} kPa L = ${fmt(p2)} × ${fmt(V2)}`, status: 'done' },
      { id: 'amount', title: `Gas in the syringe at ${BOYLE_TEMPERATURE_K} K`, formula: 'n = p₁V₁ ÷ RT', working: `n = ${fmt(p1)} kPa × ${fmt(V1)} L ÷ (${R} × ${BOYLE_TEMPERATURE_K} K) = ${fmt(n)} mol`, status: 'done' },
    );
    final = { p: p2, V: V2, n, T };
    start = { p: p1, V: V1, n, T };
    unknownVariable = 'p';
    symbols = { p: 'p₂', V: 'V₂', n: 'n', T: 'T' };
  } else if (setup.mode === 'charles') {
    const V1 = base.V1 as number;
    const T1 = base.T1 as number;
    const T2 = base.T2 as number;
    const V2 = (V1 * T2) / T1;
    const n = (CHARLES_PRESSURE_KPA * V1) / (R * T1);
    steps.push(
      { id: 'law', title: 'Charles’s law (n and p fixed)', formula: 'V₂ = V₁ × T₂ ÷ T₁ (T in kelvin)', working: `V₂ = ${fmt(V1)} L × ${formatSig(T2, 5)} K ÷ ${formatSig(T1, 5)} K = ${fmt(V2)} L`, status: 'done' },
      { id: 'amount', title: `Gas in the syringe at ${CHARLES_PRESSURE_KPA} kPa`, formula: 'n = pV₁ ÷ RT₁', working: `n = ${CHARLES_PRESSURE_KPA} kPa × ${fmt(V1)} L ÷ (${R} × ${formatSig(T1, 5)} K) = ${fmt(n)} mol`, status: 'done' },
    );
    final = { p: CHARLES_PRESSURE_KPA, V: V2, n, T: T2 };
    start = { p: CHARLES_PRESSURE_KPA, V: V1, n, T: T1 };
    unknownVariable = 'V';
    symbols = { p: 'p', V: 'V₂', n: 'n', T: 'T₂' };
  } else {
    unknownVariable = setup.unknown as GasExploreVariable;
    const known = base as Partial<Record<GasExploreVariable, number>>;
    const solved = idealSolve(unknownVariable, known);
    steps.push({
      id: 'law',
      title: 'Rearrange pV = nRT',
      formula: `${IDEAL_REARRANGED[unknownVariable]}, R = 8.314 kPa L K⁻¹ mol⁻¹`,
      working: solved.working,
      status: 'done',
    });
    final = { ...(known as Record<GasExploreVariable, number>), [unknownVariable]: solved.value };
    start = final;
    symbols = { p: 'p', V: 'V', n: 'n', T: 'T' };
  }

  const unknownSymbol = SLOT_COPY[setup.unknown].symbol;
  const answer = answerStep(unknownVariable, final[unknownVariable], setup, unknownSymbol);
  if (answer) steps.push(answer);
  const value = fromBase(unknownVariable, final[unknownVariable], setup.units);
  const unit = unitLabel(unknownVariable, setup.units);
  const note = limitNote(final, symbols);
  const withinModel = note === null;
  const shown = `${unknownSymbol} = ${unknownVariable === 'T' ? formatSig(value, 5) : fmt(value)} ${unit}`;
  if (!withinModel) steps[steps.length - 1] = { ...steps[steps.length - 1], status: 'current', note: 'outside the model' };
  return {
    unknown: setup.unknown,
    value,
    unit,
    steps,
    result: withinModel ? `Result: ${shown}` : `Result: ${shown} is outside this model. The views stay at the last result inside the model.`,
    baseValues: final,
    start,
    state: { nMol: final.n, temperatureK: final.T, volumeL: final.V },
    withinModel,
    limitNote: note,
  };
}

/* ------------------------------------------------------------ teaching goal */

const within = (value: number, min: number, max: number) => value >= min && value <= max;

/** Learning question for each Explore mode (CT-INTERACT C7). */
export const GAS_EXPLORE_QUESTIONS: Record<GasMode, string> = {
  boyle: 'When you squeeze a trapped gas, how does its pressure change?',
  charles: 'How does the volume of a gas follow its temperature, and why must the temperature be in kelvin?',
  ideal: 'How do the pressure, volume, amount and temperature of a gas fit together in pV = nRT?',
};

/**
 * Teaching tasks for each mode. Each check reads only the model result (kPa, L, mol and K), never
 * the UI, and none is met at the default setup.
 */
export const GAS_EXPLORE_CHALLENGES: Record<GasMode, ExploreChallenge<GasExploreResult>[]> = {
  boyle: [
    {
      id: 'double-volume',
      prompt: 'Let the gas expand to double its starting volume. What happens to the pressure?',
      hint: 'Make the new volume V₂ twice the starting volume V₁.',
      isMet: (result) => result.withinModel && within(result.baseValues.V / result.start.V, 1.95, 2.05),
      why: 'Doubling the volume halves the pressure: the same particles hit twice as much wall, so each part of the wall is hit half as often. The pressure falls, but it does not drop to zero.',
    },
    {
      id: 'four-times',
      prompt: 'Make the pressure 4 times the starting pressure by changing only the new volume.',
      hint: 'p × V must stay the same. What fraction of V₁ gives 4 × p₁?',
      isMet: (result) => result.withinModel && within(result.baseValues.p / result.start.p, 3.9, 4.1),
      why: 'p × V stays constant, so 4 times the pressure needs one-quarter of the volume: 100 kPa × 20 L = 2000 kPa L = 400 kPa × 5 L. Pressure and volume are inversely proportional, not a straight line.',
    },
    {
      id: 'gauge-top',
      prompt: 'Find the new volume where the pressure just reaches 1000 kPa, the top of this model (980–1000 kPa).',
      hint: 'V₂ = p₁V₁ ÷ 1000 kPa.',
      isMet: (result) => result.withinModel && within(result.baseValues.p, 980, 1000),
      why: 'V₂ = p₁V₁ ÷ p₂, so with 100 kPa × 20 L the gas reaches 1000 kPa at 2 L. Squeeze harder and real gases stop obeying Boyle’s law, because the particles’ own volume and attractions start to matter.',
    },
  ],
  charles: [
    {
      id: 'double-kelvin',
      prompt: 'Double the kelvin temperature and check what happens to the volume.',
      hint: 'Make T₂ twice T₁ in kelvin (for example 300 K → 600 K).',
      isMet: (result) => result.withinModel && within(result.baseValues.T / result.start.T, 1.98, 2.02),
      why: 'At constant pressure V ÷ T stays the same when T is in kelvin, so doubling the kelvin temperature doubles the volume: the faster particles push the plunger out until the pressure is back to 100 kPa.',
    },
    {
      id: 'double-celsius',
      prompt: 'Now double the Celsius temperature instead (for example 20 °C → 40 °C). Does the volume double?',
      hint: 'Type T₁ and T₂ in °C, with T₂ twice T₁ and T₁ at least 5 °C.',
      isMet: (result) => {
        const startC = result.start.T - KELVIN_OFFSET;
        const endC = result.baseValues.T - KELVIN_OFFSET;
        return result.withinModel && startC >= 5 && within(endC / startC, 1.95, 2.05);
      },
      why: 'No. 20 °C → 40 °C is only 293 K → 313 K, so the volume grows by about 7 %, not 100 %. The Celsius zero is not where the particles stop moving, so gas laws need kelvin.',
    },
    {
      id: 'shrink-quarter',
      prompt: 'Cool the gas until its volume shrinks to three-quarters of the start (73–77 %).',
      hint: 'V₂ ÷ V₁ = T₂ ÷ T₁ in kelvin, so T₂ = 0.75 × T₁.',
      isMet: (result) => result.withinModel && within(result.baseValues.V / result.start.V, 0.73, 0.77),
      why: 'The volume falls in step with the kelvin temperature: 0.75 × 293 K = 220 K (−53 °C). Extend the line and the volume would reach zero at 0 K (−273 °C), which is why the kelvin scale starts there.',
    },
  ],
  ideal: [
    {
      id: 'molar-volume',
      prompt: 'Find the volume of exactly 1 mol of gas at 25 °C and 100 kPa.',
      hint: 'Find V with n = 1 mol, T = 25 °C and p = 100 kPa.',
      isMet: (result) =>
        result.withinModel &&
        within(result.baseValues.n, 0.99, 1.01) &&
        within(result.baseValues.T, 297.15, 299.15) &&
        within(result.baseValues.p, 99, 101),
      why: 'V = 1 × 8.314 × 298.15 ÷ 100 = 24.79 L. Any gas, helium or carbon dioxide, fills the same 24.79 L at these conditions, because volume depends on the number of particles, not on their kind.',
    },
    {
      id: 'kelvin-pressure',
      prompt: 'Hold 1 mol in a 10 L container and find the temperature that makes the pressure 300 kPa (294–306 kPa).',
      hint: 'Choose Find p, set n = 1 mol and V = 10 L, then change T.',
      isMet: (result) =>
        result.unknown === 'p' &&
        result.withinModel &&
        within(result.baseValues.n, 0.99, 1.01) &&
        within(result.baseValues.V, 9.9, 10.1) &&
        within(result.baseValues.p, 294, 306),
      why: 'T = pV ÷ nR = 300 × 10 ÷ 8.314 = 361 K, which is 88 °C. Pressure is proportional to the kelvin temperature; using 88 as if it were kelvin would give only 73 kPa.',
    },
    {
      id: 'model-limit',
      prompt: 'Squeeze a gas so hard that pV = nRT stops: make the calculated pressure go above 1000 kPa.',
      hint: 'Choose Find p, then make V small or n large.',
      isMet: (result) => result.unknown === 'p' && !result.withinModel && result.baseValues.p > GAS_EXPLORE_LIMITS.p.max,
      why: 'For 1 mol at 25 °C, any volume below about 2.5 L gives more than 1000 kPa. At such pressures the particles’ own volume and attractions matter, so a real gas no longer behaves ideally and the model stops instead of guessing.',
    },
  ],
};

